import { useCallback, useEffect, useState } from "react";
import api from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";

// Lightweight favorites store hook — fetches once on mount, exposes mutators.
export function useFavorites() {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]); // [{fips, county, created_at}]
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setFavorites([]);
      setLoaded(true);
      return;
    }
    try {
      const { data } = await api.get("/favorites");
      setFavorites(data.favorites || []);
    } catch {
      setFavorites([]);
    } finally {
      setLoaded(true);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isFavorite = useCallback(
    (fips) => favorites.some((f) => f.fips === fips),
    [favorites]
  );

  const add = useCallback(
    async (county) => {
      await api.post(`/favorites/${county.fips}`);
      setFavorites((prev) => {
        if (prev.some((f) => f.fips === county.fips)) return prev;
        return [{ fips: county.fips, county, created_at: new Date().toISOString() }, ...prev];
      });
    },
    []
  );

  const remove = useCallback(async (fips) => {
    await api.delete(`/favorites/${fips}`);
    setFavorites((prev) => prev.filter((f) => f.fips !== fips));
  }, []);

  const toggle = useCallback(
    async (county) => {
      if (favorites.some((f) => f.fips === county.fips)) {
        await remove(county.fips);
        return false;
      }
      await add(county);
      return true;
    },
    [favorites, add, remove]
  );

  return { favorites, loaded, isFavorite, add, remove, toggle, refresh };
}
